import {
  Autocomplete,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { Option } from '@common/types';
import { Clear, Search } from '@mui/icons-material';
import { t } from 'i18next';

interface SearchableSelectProps {
  options: Option[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  searchQuery: string;
  onChangeSearchQuery: (query: string) => void;
  label: string;
  helperText?: string;
  error?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
}

export const SearchableSelect: FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  helperText,
  searchQuery,
  onChangeSearchQuery,
  label,
  error,
  placeholder,
  isLoading = false,
  required = false,
  disabled,
}) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  useEffect(() => {
    if (value === null || value === undefined) {
      setSelectedOption(null);
      return;
    }

    const match = options.find(o => o.value === value);
    if (match) {
      setSelectedOption(match);
    }
  }, [value, options]);

  const onClear = () => {
    onChangeSearchQuery('');
    setSelectedOption(null);
    onChange(null);
  };

  const noOptionsText =
    searchQuery && searchQuery.length > 2
      ? 'No options'
      : t('common.autocompletePlaceholder.enterThreeLetter');

  return (
    <>
      <Autocomplete
        sx={{
          width: '100%',
        }}
        loading={isLoading}
        disablePortal
        value={selectedOption}
        options={options}
        isOptionEqualToValue={(option, selected) =>
          option.value === selected.value
        }
        disabled={disabled}
        renderOption={(liProps, option) => (
          <li {...liProps} key={option.value}>
            <Typography
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {option.label}
            </Typography>
          </li>
        )}
        onChange={(event, newValue) => {
          setSelectedOption(newValue);
          onChange(newValue?.value ?? null);
        }}
        inputValue={searchQuery}
        onInputChange={(event, newInputValue) => {
          onChangeSearchQuery(newInputValue);
        }}
        noOptionsText={noOptionsText}
        renderInput={params => (
          <TextField
            {...params}
            label={`${label} ${required ? '*' : '(optional)'}`}
            placeholder={placeholder}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                startAdornment: (
                  <>
                    <InputAdornment position='start'>
                      <Search />
                    </InputAdornment>
                  </>
                ),
                endAdornment: (
                  <>
                    <InputAdornment position='end'>
                      {searchQuery && (
                        <IconButton
                          onClick={onClear}
                          sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                          <Clear />
                        </IconButton>
                      )}
                    </InputAdornment>
                  </>
                ),
              },
            }}
          />
        )}
      />
      {helperText && (
        <FormHelperText error={error} sx={{ mx: '14px' }}>
          {helperText}
        </FormHelperText>
      )}
    </>
  );
};

SearchableSelect.displayName = 'SearchableSelect';
